import React, { useCallback, useEffect, useState } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';
import {
  Share, ShareKind, ShareRole, createShare, deleteShare, listShares, updateShareRole,
} from '../../api/sharingApi';
import { fill } from './fill';
import './sharing.css';

interface ShareDialogProps {
  kind: ShareKind;
  id: number;
  /** The item's name, for the dialog's title */
  name: string;
  onClose: () => void;
  /** The owner isn't premium (or stopped being): show the paywall instead */
  onUpgradeRequired: () => void;
}

// Who can see one note, document or storyboard, and the way to invite
// someone else. Only its owner can open it — the endpoints behind it 404 for
// everyone else — so nothing here is gated on a role.
const ShareDialog: React.FC<ShareDialogProps> = ({ kind, id, name, onClose, onUpgradeRequired }) => {
  const { t } = useLanguage();
  const [shares, setShares] = useState<Share[] | null>(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<ShareRole>('editor');
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState('');
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      setShares(await listShares(kind, id));
    } catch {
      setShares([]);
      setError(t.sharing.loadFailed);
    }
  }, [kind, id, t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || inviting) return;
    setInviting(true);
    setError('');
    try {
      const share = await createShare(kind, id, email.trim(), role);
      // Kept from the response rather than reloading: the invite link and
      // whether the mail went out are in this answer alone.
      setShares((current) => [...(current || []), share]);
      setEmail('');
    } catch (err: any) {
      if (err.response?.status === 402) {
        onUpgradeRequired();
        return;
      }
      setError(err.response?.data?.error || t.sharing.loadFailed);
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (share: Share, next: ShareRole) => {
    const previous = share.role;
    setShares((current) => (current || []).map(
      (row) => (row.id === share.id ? { ...row, role: next } : row)));
    try {
      await updateShareRole(share.id, next);
    } catch {
      setShares((current) => (current || []).map(
        (row) => (row.id === share.id ? { ...row, role: previous } : row)));
      setError(t.sharing.loadFailed);
    }
  };

  const handleRemove = async (share: Share) => {
    const who = share.user?.name || share.email;
    if (!window.confirm(fill(t.sharing.removeConfirm, { name: who }))) return;
    try {
      await deleteShare(share.id);
      setShares((current) => (current || []).filter((row) => row.id !== share.id));
    } catch {
      setError(t.sharing.loadFailed);
    }
  };

  const copyLink = async (share: Share) => {
    if (!share.invite_url) return;
    try {
      await navigator.clipboard.writeText(share.invite_url);
      setCopiedId(share.id);
      setTimeout(() => setCopiedId((current) => (current === share.id ? null : current)), 2000);
    } catch {
      // Clipboard blocked (http, permissions): the link is on screen anyway
    }
  };

  const hasPending = (shares || []).some((share) => share.pending);

  return (
    <div className="share-overlay" onClick={onClose}>
      <div
        className="share-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={fill(t.sharing.shareTitle, { name })}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="share-dialog-header">
          <h2>{fill(t.sharing.shareTitle, { name })}</h2>
          <button type="button" className="share-close" onClick={onClose} aria-label={t.sharing.close}>✕</button>
        </div>

        {error && <div className="share-error">{error}</div>}

        <form className="share-invite" onSubmit={handleInvite}>
          <label className="share-label" htmlFor="share-invite-email">{t.sharing.inviteByEmail}</label>
          <div className="share-invite-row">
            <input
              id="share-invite-email"
              type="email"
              className="share-email-input"
              value={email}
              placeholder={t.sharing.emailPlaceholder}
              onChange={(e) => setEmail(e.target.value)}
            />
            <select
              className="share-role-select"
              value={role}
              aria-label={t.sharing.inviteByEmail}
              onChange={(e) => setRole(e.target.value as ShareRole)}
            >
              <option value="editor">{t.sharing.canEdit}</option>
              <option value="viewer">{t.sharing.canView}</option>
            </select>
            <button type="submit" className="share-invite-btn" disabled={inviting || !email.trim()}>
              {inviting ? t.sharing.inviting : t.sharing.invite}
            </button>
          </div>
        </form>

        <h3 className="share-section-title">{t.sharing.peopleWithAccess}</h3>
        {shares === null ? (
          <p className="share-empty">{t.common.loading}</p>
        ) : shares.length === 0 ? (
          <p className="share-empty">{t.sharing.onlyYou}</p>
        ) : (
          <ul className="share-list">
            {shares.map((share) => (
              <li key={share.id} className="share-row">
                <div className="share-row-main">
                  <span className="share-person">{share.user?.name || share.email}</span>
                  {share.pending && <span className="share-pending">{t.sharing.pending}</span>}
                  <select
                    className="share-role-select"
                    value={share.role}
                    aria-label={share.user?.name || share.email}
                    onChange={(e) => handleRoleChange(share, e.target.value as ShareRole)}
                  >
                    <option value="editor">{t.sharing.canEdit}</option>
                    <option value="viewer">{t.sharing.canView}</option>
                  </select>
                  <button
                    type="button"
                    className="share-remove"
                    onClick={() => handleRemove(share)}
                  >
                    {t.sharing.remove}
                  </button>
                </div>
                {share.invite_url && (
                  <div className="share-invite-link">
                    <input type="text" readOnly value={share.invite_url} aria-label={t.sharing.copyLink} />
                    <button type="button" className="share-copy" onClick={() => copyLink(share)}>
                      {copiedId === share.id ? t.sharing.copied : t.sharing.copyLink}
                    </button>
                    <span className="share-emailed">
                      {share.emailed ? t.sharing.emailedLink : t.sharing.sendThemLink}
                    </span>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        {hasPending && <p className="share-hint">{t.sharing.pendingExplain}</p>}
      </div>
    </div>
  );
};

export default ShareDialog;
